/**
 * Title reader — who reads listing titles (Jev or our own title models), switchable here without a
 * deploy, and how the two compare: a summary per question over a window, and the newest reads side by
 * side. Rows come from reader_compare (backend: modules/ai/reader-compare.repository.ts), written in
 * watch mode for every listing and whenever our models read on their own.
 *
 * Below it, the same for the product pick (which catalogue product a listing sells): Jev or our own
 * picker, rows from pick_compare (backend: modules/catalogue/pick-compare.repository.ts).
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, RotateCcw } from 'lucide-react';
import ChartCard from '@/components/ChartCard';
import { cn } from '@/lib/cn';
import { Stat, WindowToggle } from './pipeline/PipelinePanels';
import {
  fetchPickRecent, fetchPickSummary, fetchReaderConfig, fetchReaderRecent, fetchReaderSummary, saveReaderConfig,
  type CompareField, type PickCompareRow, type PickSide, type PickSummary, type ReadSide, type ReaderCompareRow,
  type ReaderConfigResponse, type ReaderMode, type ReaderSummary,
} from '@/api/reader';

const MODE_TEXT: Record<ReaderMode, { label: string; text: string }> = {
  jev: { label: 'Jev', text: 'Jev reads every title. Our models read only when Jev cannot (down, over budget).' },
  watch: { label: 'Jev + watch', text: 'Jev decides. Our models read every title too; both answers are stored and compared below. Costs nothing extra.' },
  own: { label: 'Our models', text: 'Our models read and decide. A listing they do not all agree on is held, never sent. Jev only if our service cannot be asked.' },
};
const PICK_TEXT: Record<ReaderMode, { label: string; text: string }> = {
  jev: { label: 'Jev', text: 'Jev picks the product. Our picker picks only when Jev cannot (down, over budget).' },
  watch: { label: 'Jev + watch', text: 'Jev decides. Our picker also picks every listing Jev is asked about; both are stored and compared below. Costs nothing extra.' },
  own: { label: 'Our picker', text: 'Our picker decides, at the same 0.8 bar. Jev is not asked (no pick or condition calls). Jev only if our picker cannot be asked.' },
};
const FIELDS: Array<{ key: CompareField; label: string }> = [
  { key: 'category', label: 'Category' },
  { key: 'product_type', label: 'Item type' },
  { key: 'single_model', label: 'One model' },
  { key: 'brand', label: 'Make' },
];
const pct = (a: number, b: number) => (b > 0 ? `${((a / b) * 100).toFixed(1)}%` : '—');
const n0 = (n: number) => n.toLocaleString();

function side(s: ReadSide | null): string {
  if (!s) return '—';
  const one = s.single_model === false ? ' · several' : '';
  return `${s.category ?? 'no category'} · ${s.product_type ?? '?'} · ${s.brand ?? 'no make'}${one}`;
}

function ReaderSetting() {
  const qc = useQueryClient();
  const q = useQuery<ReaderConfigResponse>({ queryKey: ['reader', 'config'], queryFn: fetchReaderConfig, refetchInterval: 60_000 });
  const [mode, setMode] = useState<ReaderMode>('jev');
  const [models, setModels] = useState('');
  const [sure, setSure] = useState('0.95');
  const [note, setNote] = useState('');
  useEffect(() => {
    if (!q.data) return;
    setMode(q.data.config.mode);
    setModels(q.data.config.models.join(', '));
    setSure(String(q.data.config.sure));
  }, [q.data]);
  const save = useMutation({
    mutationFn: (body: Parameters<typeof saveReaderConfig>[0]) => saveReaderConfig(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reader'] }),
  });
  const c = q.data?.config;
  const svc = q.data?.titleService;
  const sureNum = Number(sure);
  const modelList = models.split(',').map(m => m.trim()).filter(Boolean);
  const changed = !!c && (mode !== c.mode || modelList.join(',') !== c.models.join(',') || sureNum !== c.sure);

  return (
    <ChartCard title="Reader setting" isLoading={q.isLoading} isError={q.isError}
               subtitle="Takes effect within a minute, no deploy. Every change is recorded with a note.">
      {c && svc && (
        <div className="space-y-3 text-xs">
          <div className="flex flex-wrap gap-2">
            {(q.data!.modes).map(m => (
              <button key={m} onClick={() => setMode(m)}
                      className={cn('rounded border px-3 py-2 text-left', mode === m ? 'border-sky-500 bg-sky-950/40 text-slate-100' : 'border-slate-800 bg-slate-950/40 text-slate-400 hover:border-slate-700')}>
                <div className="font-semibold">{MODE_TEXT[m].label}{c.mode === m && <span className="ml-1 text-emerald-400">(now)</span>}</div>
                <div className="mt-0.5 max-w-xs text-[11px] leading-snug text-slate-500">{MODE_TEXT[m].text}</div>
              </button>
            ))}
          </div>
          <div className={cn('rounded border px-2 py-1', svc.configured && svc.reachable ? 'border-slate-800 text-slate-400' : 'border-amber-700 text-amber-300')}>
            Our title service: {!svc.configured ? 'not configured (TITLE_CLASSIFIER_URL) — "Our models" and "watch" behave like Jev'
              : svc.reachable ? 'reachable' : 'marked down after a failed call — Jev and the backup read meanwhile'}
            {svc.loaded && <span className="text-slate-500"> · loaded: {svc.loaded.models.join(', ') || 'no title models'}</span>}
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            <label className="block">
              <span className="text-slate-500">Models that must all agree (lead first)</span>
              <input value={models} onChange={e => setModels(e.target.value)}
                     className="mt-1 w-full rounded border border-slate-800 bg-slate-950 px-2 py-1 text-slate-200" />
            </label>
            <label className="block">
              <span className="text-slate-500">How sure each must be (0.5–0.999)</span>
              <input value={sure} onChange={e => setSure(e.target.value)} inputMode="decimal"
                     className="mt-1 w-full rounded border border-slate-800 bg-slate-950 px-2 py-1 text-slate-200" />
            </label>
            <label className="block">
              <span className="text-slate-500">Note (why)</span>
              <input value={note} onChange={e => setNote(e.target.value)}
                     className="mt-1 w-full rounded border border-slate-800 bg-slate-950 px-2 py-1 text-slate-200" />
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button disabled={!changed || save.isPending || !(sureNum >= 0.5 && sureNum <= 0.999) || !modelList.length}
                    onClick={() => save.mutate({ mode, models: modelList, sure: sureNum, note: note || undefined })}
                    className="inline-flex items-center gap-1 rounded bg-sky-700 px-3 py-1 text-slate-100 disabled:opacity-40">
              {save.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
            </button>
            {c.source === 'observe' && (
              <button onClick={() => save.mutate({ reset: true })}
                      className="inline-flex items-center gap-1 rounded bg-slate-800 px-3 py-1 text-slate-300">
                <RotateCcw className="h-3 w-3" /> Back to the server default ({q.data!.env.mode})
              </button>
            )}
            <span className="text-slate-500">
              {c.source === 'observe' ? `Set by ${c.updatedBy ?? '?'} ${c.updatedAt ? new Date(c.updatedAt).toLocaleString() : ''}${c.note ? ` — ${c.note}` : ''}` : 'Server default (env); not changed here yet.'}
            </span>
            {save.isError && <span className="text-red-400">{String((save.error as Error)?.message ?? 'save failed')}</span>}
          </div>
        </div>
      )}
    </ChartCard>
  );
}

function SummaryCard({ hours }: { hours: number }) {
  const q = useQuery<ReaderSummary>({ queryKey: ['reader', 'summary', hours], queryFn: () => fetchReaderSummary(hours), refetchInterval: 60_000 });
  const s = q.data;
  return (
    <ChartCard title="Summary" subtitle="Reads our models took part in. 'Both read' = Jev and our models read the same listing (watch mode)."
               isLoading={q.isLoading} isError={q.isError} isEmpty={!!s && s.reads === 0}
               emptyLabel="No reads with our models in this window. Switch to 'Jev + watch' to start comparing.">
      {s && s.reads > 0 && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="Reads" value={n0(s.reads)} hint={Object.entries(s.decidedBy).map(([k, v]) => `${k} decided ${n0(v)}`).join(' · ')} />
            <Stat label="Both read" value={n0(s.bothRead)} hint="Jev and our models, same listing" />
            <Stat label="All four the same" value={pct(s.allSame, s.bothRead)} tone={s.bothRead && s.allSame / s.bothRead >= 0.95 ? 'good' : 'warn'} />
            <Stat label="Our models settled" value={pct(s.ownSettled, s.ownRead)} hint="all agreed and sure; the rest would be held" />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {FIELDS.map(f => (
              <Stat key={f.key} label={`${f.label} same`} value={pct(s.same[f.key].same, s.same[f.key].of)}
                    hint={`${n0(s.same[f.key].same)} of ${n0(s.same[f.key].of)}`} />
            ))}
          </div>
          {(s.unsettledTop.length > 0 || Object.keys(s.jevErrors).length > 0) && (
            <div className="grid gap-3 text-xs sm:grid-cols-2">
              <div>
                <div className="mb-1 text-slate-500">Why our models were not settled</div>
                {s.unsettledTop.map(u => <div key={u.reason} className="flex justify-between text-slate-300"><span>{u.reason}</span><span className="tabular-nums">{n0(u.n)}</span></div>)}
              </div>
              <div>
                <div className="mb-1 text-slate-500">Jev could not answer</div>
                {Object.entries(s.jevErrors).map(([e, n]) => <div key={e} className="flex justify-between text-slate-300"><span>{e}</span><span className="tabular-nums">{n0(n)}</span></div>)}
              </div>
            </div>
          )}
        </div>
      )}
    </ChartCard>
  );
}

function RecentCard() {
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [limit, setLimit] = useState(100);
  const q = useQuery<ReaderCompareRow[]>({ queryKey: ['reader', 'recent', limit], queryFn: () => fetchReaderRecent(limit), refetchInterval: 60_000 });
  const rows = (q.data ?? []).filter(r => !onlyDiff || (r.same && Object.values(r.same).some(v => !v)) || r.own?.agree === false);
  return (
    <ChartCard title={`Last ${limit} reads`} isLoading={q.isLoading} isError={q.isError} isEmpty={!!q.data && q.data.length === 0}
               emptyLabel="Nothing yet."
               actions={
                 <div className="flex items-center gap-2 text-xs">
                   <label className="flex items-center gap-1 text-slate-400">
                     <input type="checkbox" checked={onlyDiff} onChange={e => setOnlyDiff(e.target.checked)} /> only differences
                   </label>
                   {[100, 500].map(n => (
                     <button key={n} onClick={() => setLimit(n)}
                             className={cn('rounded px-2 py-1', limit === n ? 'bg-slate-700 text-slate-100' : 'bg-slate-900 text-slate-400')}>{n}</button>
                   ))}
                 </div>
               }>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-xs">
          <thead className="text-slate-500"><tr className="border-b border-slate-800">
            <th className="py-1 text-left font-medium">When</th>
            <th className="py-1 text-left font-medium">Title</th>
            <th className="py-1 text-left font-medium">Jev</th>
            <th className="py-1 text-left font-medium">Our models</th>
            <th className="py-1 text-left font-medium">Differs on</th>
            <th className="py-1 text-left font-medium">Decided</th>
          </tr></thead>
          <tbody className="text-slate-300">
            {rows.map((r, i) => {
              const diff = r.same ? FIELDS.filter(f => !r.same![f.key]).map(f => f.label) : [];
              return (
                <tr key={`${r.identity_ref}-${r.ts}-${i}`} className="border-b border-slate-900 align-top">
                  <td className="whitespace-nowrap py-1 pr-2 text-slate-500">{new Date(r.ts).toLocaleTimeString()}</td>
                  <td className="py-1 pr-2"><div className="max-w-[320px] break-words">{r.title}</div><div className="text-[11px] text-slate-500">{r.source}</div></td>
                  <td className="py-1 pr-2">{r.jev ? side(r.jev) : <span className="text-slate-500">{r.jev_error ? `could not answer (${r.jev_error})` : '—'}</span>}</td>
                  <td className="py-1 pr-2">
                    {side(r.own)}
                    {r.own && <div className={cn('text-[11px]', r.own.agree ? 'text-emerald-400' : 'text-amber-400')}>
                      {r.own.agree ? 'all agreed' : `not settled: ${r.own.unsettled.slice(0, 3).join(', ')}`}</div>}
                  </td>
                  <td className={cn('py-1 pr-2', diff.length ? 'text-amber-300' : 'text-slate-500')}>{r.same ? (diff.length ? diff.join(', ') : 'same') : '—'}</td>
                  <td className="py-1">{r.decided_by}{r.why ? <div className="text-[11px] text-slate-500">{r.why}</div> : null}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
}

function pickSide(s: PickSide | null): string {
  if (!s) return '—';
  if (s.modelKey) return `${s.modelKey} (${s.p.toFixed(2)})`;
  const why = s.reason === 'not_in_catalogue' ? 'none of these' : s.reason === 'low_probability' ? 'not sure' : s.reason;
  return `${why} (${s.p.toFixed(2)})`;
}

function PickSetting() {
  const qc = useQueryClient();
  const q = useQuery<ReaderConfigResponse>({ queryKey: ['reader', 'config'], queryFn: fetchReaderConfig, refetchInterval: 60_000 });
  const [pick, setPick] = useState<ReaderMode>('jev');
  const [note, setNote] = useState('');
  useEffect(() => { if (q.data) setPick(q.data.config.pick); }, [q.data]);
  const save = useMutation({
    mutationFn: (body: Parameters<typeof saveReaderConfig>[0]) => saveReaderConfig(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reader'] }),
  });
  const c = q.data?.config;
  const svc = q.data?.titleService;
  const picker = svc?.loaded?.picker ?? null;
  return (
    <ChartCard title="Product pick setting" isLoading={q.isLoading} isError={q.isError}
               subtitle="Which catalogue product a listing sells, or none of them. Takes effect within a minute, no deploy.">
      {c && svc && (
        <div className="space-y-3 text-xs">
          <div className="flex flex-wrap gap-2">
            {(q.data!.modes).map(m => (
              <button key={m} onClick={() => setPick(m)}
                      className={cn('rounded border px-3 py-2 text-left', pick === m ? 'border-sky-500 bg-sky-950/40 text-slate-100' : 'border-slate-800 bg-slate-950/40 text-slate-400 hover:border-slate-700')}>
                <div className="font-semibold">{PICK_TEXT[m].label}{c.pick === m && <span className="ml-1 text-emerald-400">(now)</span>}</div>
                <div className="mt-0.5 max-w-xs text-[11px] leading-snug text-slate-500">{PICK_TEXT[m].text}</div>
              </button>
            ))}
          </div>
          <div className={cn('rounded border px-2 py-1', picker ? 'border-slate-800 text-slate-400' : 'border-amber-700 text-amber-300')}>
            Our picker: {!svc.configured ? 'title service not configured — "Our picker" and "watch" behave like Jev'
              : picker ? `${picker} loaded` : 'not loaded in the title service — Jev picks in every mode'}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (why)"
                   className="w-64 rounded border border-slate-800 bg-slate-950 px-2 py-1 text-slate-200" />
            <button disabled={pick === c.pick || save.isPending} onClick={() => save.mutate({ pick, note: note || undefined })}
                    className="inline-flex items-center gap-1 rounded bg-sky-700 px-3 py-1 text-slate-100 disabled:opacity-40">
              {save.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
            </button>
            <span className="text-slate-500">Server default: {q.data!.env.pick}</span>
            {save.isError && <span className="text-red-400">{String((save.error as Error)?.message ?? 'save failed')}</span>}
          </div>
        </div>
      )}
    </ChartCard>
  );
}

function PickSummaryCard({ hours }: { hours: number }) {
  const q = useQuery<PickSummary>({ queryKey: ['reader', 'pick-summary', hours], queryFn: () => fetchPickSummary(hours), refetchInterval: 60_000 });
  const s = q.data;
  return (
    <ChartCard title="Product pick summary" subtitle="Picks our picker took part in. 'Both picked' = Jev and our picker, same listing (watch mode). Same = same product, or neither picked one."
               isLoading={q.isLoading} isError={q.isError} isEmpty={!!s && s.picks === 0}
               emptyLabel="No picks with our picker in this window. Switch the pick to 'Jev + watch' to start comparing.">
      {s && s.picks > 0 && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="Picks" value={n0(s.picks)} hint={Object.entries(s.decidedBy).map(([k, v]) => `${k} decided ${n0(v)}`).join(' · ')} />
            <Stat label="Both picked" value={n0(s.both)} hint="Jev and our picker, same listing" />
            <Stat label="Same outcome" value={pct(s.samePicked + s.sameNone, s.both)}
                  tone={s.both && (s.samePicked + s.sameNone) / s.both >= 0.95 ? 'good' : 'warn'}
                  hint={`${n0(s.samePicked)} same product · ${n0(s.sameNone)} neither`} />
            <Stat label="Different product" value={n0(s.different)} tone={s.different ? 'warn' : 'good'} hint="both picked, not the same one" />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="Only Jev picked" value={n0(s.onlyJevPicked)} hint="ours said none / not sure" />
            <Stat label="Only ours picked" value={n0(s.onlyOwnPicked)} hint="Jev said none / not sure" />
          </div>
          {Object.keys(s.jevErrors).length > 0 && (
            <div className="text-xs">
              <div className="mb-1 text-slate-500">Jev could not pick (ours stood in)</div>
              {Object.entries(s.jevErrors).map(([e, n]) => <div key={e} className="flex justify-between text-slate-300"><span>{e}</span><span className="tabular-nums">{n0(n)}</span></div>)}
            </div>
          )}
        </div>
      )}
    </ChartCard>
  );
}

function PickRecentCard() {
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [limit, setLimit] = useState(100);
  const q = useQuery<PickCompareRow[]>({ queryKey: ['reader', 'pick-recent', limit], queryFn: () => fetchPickRecent(limit), refetchInterval: 60_000 });
  const rows = (q.data ?? []).filter(r => !onlyDiff || r.same === false);
  return (
    <ChartCard title={`Last ${limit} product picks`} isLoading={q.isLoading} isError={q.isError} isEmpty={!!q.data && q.data.length === 0}
               emptyLabel="Nothing yet."
               actions={
                 <div className="flex items-center gap-2 text-xs">
                   <label className="flex items-center gap-1 text-slate-400">
                     <input type="checkbox" checked={onlyDiff} onChange={e => setOnlyDiff(e.target.checked)} /> only differences
                   </label>
                   {[100, 500].map(n => (
                     <button key={n} onClick={() => setLimit(n)}
                             className={cn('rounded px-2 py-1', limit === n ? 'bg-slate-700 text-slate-100' : 'bg-slate-900 text-slate-400')}>{n}</button>
                   ))}
                 </div>
               }>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-xs">
          <thead className="text-slate-500"><tr className="border-b border-slate-800">
            <th className="py-1 text-left font-medium">When</th>
            <th className="py-1 text-left font-medium">Title</th>
            <th className="py-1 text-left font-medium">Jev</th>
            <th className="py-1 text-left font-medium">Our picker</th>
            <th className="py-1 text-left font-medium">Same</th>
            <th className="py-1 text-left font-medium">Decided</th>
          </tr></thead>
          <tbody className="text-slate-300">
            {rows.map((r, i) => (
              <tr key={`${r.deal_id}-${r.ts}-${i}`} className="border-b border-slate-900 align-top">
                <td className="whitespace-nowrap py-1 pr-2 text-slate-500">{new Date(r.ts).toLocaleTimeString()}</td>
                <td className="py-1 pr-2">
                  <div className="max-w-[320px] break-words">{r.title}</div>
                  <div className="text-[11px] text-slate-500">{r.source} · {r.category} · {r.brand ?? 'no make'} · {r.candidates} candidates</div>
                </td>
                <td className="py-1 pr-2">{r.jev ? pickSide(r.jev) : <span className="text-slate-500">{r.jev_error ? `could not pick (${r.jev_error})` : '—'}</span>}</td>
                <td className="py-1 pr-2">{pickSide(r.own)}</td>
                <td className={cn('py-1 pr-2', r.same === false ? 'text-amber-300' : 'text-slate-500')}>{r.same === null ? '—' : r.same ? 'same' : 'differs'}</td>
                <td className="py-1">{r.decided_by}{r.why ? <div className="text-[11px] text-slate-500">{r.why}</div> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
}

export default function Reader() {
  const [hours, setHours] = useState<number>(24);
  return (
    <div className="space-y-4 p-4">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold text-slate-100">Title reader</h1>
          <p className="text-xs text-slate-500">
            Who reads listing titles (category, item type, one model or several, make), who picks the exact
            catalogue product, and how Jev and our own models compare on each. Condition questions are asked of
            Jev, after its own sure pick.
          </p>
        </div>
        <WindowToggle hours={hours} onChange={setHours} />
      </header>
      <ReaderSetting />
      <SummaryCard hours={hours} />
      <RecentCard />
      <PickSetting />
      <PickSummaryCard hours={hours} />
      <PickRecentCard />
    </div>
  );
}
